import { PersistConfig, persistReducer } from 'redux-persist';
import { createReducer, getType } from 'typesafe-actions';

import { Order, Pagination, ActionTypes, OrderCheck } from '@lib/domain/order';
import { OrderActions } from '@lib/state/_actions';
import { produce } from 'immer';
import storage from '@lib/state/storage';

import { OrderState, OrderInfo, OrderUpsell, OrderUserInfo } from './types';
import { Coupon } from '@lib/domain/coupon';
const stateKey = 'order';

/* ------------- Initial State ------------- */
const INITIAL_STATE: OrderState = {
    orders: [] as Order[],
    orderPost: undefined,
    currentOrder: undefined,
    orderInfo: {
        followers: {
            quantity: 0,
            price: 0,
        },
        plays: {
            quantity: 0,
            price: 0,
        },
        monthlyListeners: {
            quantity: 0,
            price: 0,
        },
        saves: {
            quantity: 0,
            price: 0,
        },
        playsSelectPost: {
            quantity: 0,
            price: 0,
        },
        savesSelectPost: {
            quantity: 0,
            price: 0,
        },
        total: 0,
        totalSelectPost: 0,
    },
    isNew: false,
    orderIDs: [],
    urlSelected: [],
    pagination: {
        total: 0,
        page_index: 1,
    },
    checkedList: [],
    checkedPosts: {} as Record<string, Record<ActionTypes, number>>,
    channel: null,
    user: null,
    upsell: { active: false, selected: false, status: false },
    loading: false,
    upsellSuccess: false,
    coupon: null,
    currentOrderId: null,
};

/* ------------- Reducers ------------- */
const setOrderInfo = (
    state: OrderState,
    {
        payload: { orderInfo, isUser },
    }: ReturnType<typeof OrderActions.setOrderInfo>,
): OrderState =>
    produce(state, (draft) => {
        draft.orderInfo = orderInfo;
        if (isUser) {
            draft.urlSelected = [];
        }
    });

const setUrlSelectPost = (
    state: OrderState,
    {
        payload: { urlSelected },
    }: ReturnType<typeof OrderActions.setUrlSelectPost>,
): OrderState =>
    produce(state, (draft) => {
        draft.urlSelected = urlSelected;
    });

const addOrderIdNotLoggedIn = (
    state: OrderState,
    {
        payload: { orderId },
    }: ReturnType<typeof OrderActions.addOrderIdNotLoggedIn>,
): OrderState =>
    produce(state, (draft) => {
        if (typeof orderId === 'string') {
            if (
                !draft?.orderIDs?.length ||
                draft.orderIDs.indexOf(orderId) < 0
            ) {
                draft.orderIDs = [orderId, ...draft.orderIDs];
            }
        } else if (orderId.length > 0) {
            draft.orderIDs = [...draft.orderIDs, ...orderId];
        } else {
            draft.orderIDs = orderId;
        }
    });

const getOrders = (
    state: OrderState,
    {
        payload: { pagination, data },
    }: ReturnType<typeof OrderActions.getOrders>,
): OrderState =>
    produce(state, (draft) => {
        draft.pagination = pagination;
        draft.orders = data;
    });

const setIsNewOrder = (
    state: OrderState,
    { payload: { isNew } }: ReturnType<typeof OrderActions.setIsNewOrder>,
): OrderState =>
    produce(state, (draft) => {
        draft.isNew = isNew;
    });

const setPageIndex = (
    state: OrderState,
    { payload: { pageIndex } }: ReturnType<typeof OrderActions.setPageIndex>,
): OrderState =>
    produce(state, (draft) => {
        draft.pagination.page_index = pageIndex;
    });

const checkOrder = (
    state: OrderState,
    { payload: { checkedList } }: ReturnType<typeof OrderActions.checkOrder>,
): OrderState =>
    produce(state, (draft) => {
        draft.checkedList = checkedList;
    });

const setCheckPosts = (
    state: OrderState,
    {
        payload: { checkedPosts },
    }: ReturnType<typeof OrderActions.setCheckPosts>,
): OrderState =>
    produce(state, (draft) => {
        draft.checkedPosts = checkedPosts;
    });

const resetOrder = (state: OrderState): OrderState =>
    produce(state, (draft) => {
        draft.urlSelected = [];
        draft.orderInfo = INITIAL_STATE.orderInfo;
        draft.coupon = null;
        draft.currentOrderId = null;
        draft.checkedPosts = null;
        draft.urlSelected = null;
    });

const setChannel = (
    state: OrderState,
    { payload }: ReturnType<typeof OrderActions.setChannel>,
): OrderState =>
    produce(state, (draft) => {
        draft.channel = payload;
    });
const setOrderUserInfo = (
    state: OrderState,
    { payload }: ReturnType<typeof OrderActions.setOrderUserInfo>,
): OrderState =>
    produce(state, (draft) => {
        draft.user = payload;
        draft.upsell = {
            active: false,
            selected: false,
            ...payload,
        };
        draft.loading = false;
    });

const setUpsell = (
    state: OrderState,
    { payload }: ReturnType<typeof OrderActions.setUpsell>,
): OrderState =>
    produce(state, (draft) => {
        draft.upsell = {
            ...draft.upsell,
            ...payload,
        };
        draft.upsellSuccess = payload.selected;
    });

const setLoading = (
    state: OrderState,
    { payload }: ReturnType<typeof OrderActions.setLoading>,
): OrderState =>
    produce(state, (draft) => {
        draft.loading = payload;
    });

const setCoupon = (
    state: OrderState,
    { payload }: ReturnType<typeof OrderActions.setCoupon>,
): OrderState =>
    produce(state, (draft) => {
        draft.coupon = payload;
    });
const setCurrentOrderId = (
    state: OrderState,
    { payload }: ReturnType<typeof OrderActions.setCurrentOrderId>,
): OrderState =>
    produce(state, (draft) => {
        draft.currentOrderId = payload;
    });
/* ------------- Hookup Reducers To Types ------------- */
const reducer = createReducer(INITIAL_STATE, {
    [getType(OrderActions.setOrderInfo)]: setOrderInfo,
    [getType(OrderActions.setUrlSelectPost)]: setUrlSelectPost,
    [getType(OrderActions.addOrderIdNotLoggedIn)]: addOrderIdNotLoggedIn,
    [getType(OrderActions.getOrders)]: getOrders,
    [getType(OrderActions.resetOrder)]: resetOrder,
    [getType(OrderActions.setIsNewOrder)]: setIsNewOrder,
    [getType(OrderActions.checkOrder)]: checkOrder,
    [getType(OrderActions.setCheckPosts)]: setCheckPosts,
    [getType(OrderActions.setPageIndex)]: setPageIndex,
    [getType(OrderActions.setChannel)]: setChannel,
    [getType(OrderActions.setOrderUserInfo)]: setOrderUserInfo,
    [getType(OrderActions.setUpsell)]: setUpsell,
    [getType(OrderActions.setLoading)]: setLoading,
    [getType(OrderActions.setCoupon)]: setCoupon,
    [getType(OrderActions.setCurrentOrderId)]: setCurrentOrderId,
});

const persistConfig: PersistConfig<OrderState> = {
    key: stateKey,
    blacklist: ['upsellSuccess'],
    storage,
};

const persistedReducer = persistReducer<OrderState, OrderActions.OrderActions>(
    persistConfig,
    reducer,
);

/* ------------- Selectors ------------- */
// use any for root state for now because of Dependency cycle error
// TODO: resolve dependency cycle error
const getReducerState = (state: any): OrderState => state[stateKey];

const selectors = {
    getOrder: (state: OrderState): Order[] => state.orders,
    getOrderIds: (state: OrderState): string[] => state.orderIDs,
    getOrderInfo: (state: OrderState): OrderInfo => state.orderInfo,
    getUrlSelectPost: (state: OrderState): string[] => state.urlSelected,
    getIsNew: (state: OrderState): boolean => state.isNew,
    getPagination: (state: OrderState): Pagination => state.pagination,
    getCheckedList: (state: OrderState): OrderCheck[] => state.checkedList,
    getCheckedPosts: (
        state: OrderState,
    ): Record<string, Record<ActionTypes, number>> => state.checkedPosts,
    getOrderUserInfo: (state: OrderState): OrderUserInfo | null => state.user,
    getUpsell: (state: OrderState): OrderUpsell => state.upsell,
    getLoading: (state: OrderState): boolean => state.loading,
    getUpsellSuccess: (state: OrderState): boolean => state.upsellSuccess,
    getCoupon: (state: OrderState): Coupon | null => state.coupon,
    getCurrentOrderId: (state: OrderState): string => state.currentOrderId,
};

/* ------------- Export ------------- */
const reducerObject = {
    // default export
    selectors,

    INITIAL_STATE,

    stateKey,
    getReducerState,
    reducer: persistedReducer,
};

export default reducerObject;
